/**
 * mdlondon Klaviyo Marketing Integration
 * Handles email signup, exit-intent popups, and newsletter subscriptions
 */

class KlaviyoIntegration {
  constructor() {
    this.klaviyoPublicKey = window.klaviyoPublicKey || null;
    this.listId = window.klaviyoListId || null;
    this.exitIntentShown = false;
    this.init();
  }

  init() {
    this.setupEmailSignupForms();
    this.setupExitIntent();
    this.setupNewsletterForms();
  }

  // Email Signup Forms
  setupEmailSignupForms() {
    document.addEventListener('submit', (e) => {
      if (e.target.classList.contains('klaviyo-signup-form')) {
        e.preventDefault();
        this.handleEmailSignup(e.target);
      }
    });
  }

  async handleEmailSignup(form) {
    const formData = new FormData(form);
    const email = formData.get('email');
    const firstName = formData.get('first_name') || '';
    const lastName = formData.get('last_name') || '';
    const source = formData.get('source') || 'website';

    const submitButton = form.querySelector('[type="submit"]');
    const originalText = submitButton.textContent;
    submitButton.disabled = true;
    submitButton.textContent = 'Subscribing...';

    try {
      await this.subscribeToKlaviyo({
        email,
        firstName,
        lastName,
        source
      });

      this.showSuccess('Thank you for subscribing!');
      form.reset();

      // Track event
      this.trackKlaviyoEvent('Newsletter Signup', {
        email,
        source
      });

      // Hide form or show success message
      const successMessage = form.parentElement.querySelector('.signup-success');
      if (successMessage) {
        form.style.display = 'none';
        successMessage.style.display = 'block';
      }

    } catch (error) {
      console.error('Klaviyo subscription error:', error);
      this.showError('Failed to subscribe. Please try again.');
    } finally {
      submitButton.disabled = false;
      submitButton.textContent = originalText;
    }
  }

  // Exit Intent Popup
  setupExitIntent() {
    if (this.getCookie('exit_intent_shown')) {
      return; // Already shown in this session
    }

    let exitIntentTriggered = false;

    document.addEventListener('mouseleave', (e) => {
      if (exitIntentTriggered || this.exitIntentShown) return;

      // Only trigger when mouse leaves from top
      if (e.clientY < 10) {
        exitIntentTriggered = true;
        this.exitIntentShown = true;
        this.showExitIntentPopup();
        this.setCookie('exit_intent_shown', 'true', 1); // 1 day
      }
    });

    // Also trigger after 30 seconds on page
    setTimeout(() => {
      if (!this.exitIntentShown && !this.getCookie('exit_intent_shown')) {
        this.exitIntentShown = true;
        this.showExitIntentPopup();
        this.setCookie('exit_intent_shown', 'true', 1);
      }
    }, 30000);
  }

  showExitIntentPopup() {
    const popup = document.createElement('div');
    popup.className = 'exit-intent-modal';
    popup.innerHTML =
      '<div class="exit-intent-dialog">' +
        '<button class="exit-intent-close" aria-label="Close">&times;</button>' +
        '<div class="exit-intent-content">' +
          '<h2>Wait! Don\'t Miss Out</h2>' +
          '<p>Subscribe to our newsletter and get <strong>10% off</strong> your first order</p>' +
          '<form class="klaviyo-signup-form exit-intent-form">' +
            '<input type="hidden" name="source" value="exit-intent">' +
            '<input type="email" name="email" placeholder="Enter your email" required>' +
            '<button type="submit">Get My Discount</button>' +
          '</form>' +
          '<p class="exit-intent-privacy">We respect your privacy. Unsubscribe anytime.</p>' +
        '</div>' +
      '</div>';

    document.body.appendChild(popup);

    setTimeout(() => popup.classList.add('exit-intent-modal--visible'), 10);

    // Close handlers
    const closeButton = popup.querySelector('.exit-intent-close');
    closeButton.addEventListener('click', () => this.closeExitIntent(popup));

    popup.addEventListener('click', (e) => {
      if (e.target === popup) {
        this.closeExitIntent(popup);
      }
    });

    // Track event
    this.trackKlaviyoEvent('Exit Intent Shown', {});
  }

  closeExitIntent(popup) {
    popup.classList.remove('exit-intent-modal--visible');
    setTimeout(() => popup.remove(), 300);
  }

  // Newsletter Forms
  setupNewsletterForms() {
    document.querySelectorAll('.newsletter-form').forEach(form => {
      if (!form.classList.contains('klaviyo-signup-form')) {
        form.classList.add('klaviyo-signup-form');
      }
    });
  }

  // Klaviyo API Integration
  async subscribeToKlaviyo(data) {
    if (!this.klaviyoPublicKey || !this.listId) {
      console.warn('Klaviyo not configured. Storing subscription locally.');
      this.storeSubscriptionLocally(data);
      return;
    }

    const payload = {
      api_key: this.klaviyoPublicKey,
      profiles: [{
        email: data.email,
        $consent: ['email'],
        $first_name: data.firstName,
        $last_name: data.lastName,
        properties: {
          'Signup Source': data.source,
          'Signup Date': new Date().toISOString()
        }
      }]
    };

    const response = await fetch('https://a.klaviyo.com/api/v2/list/' + this.listId + '/subscribe', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      throw new Error('Klaviyo API error');
    }

    return response.json();
  }

  trackKlaviyoEvent(eventName, properties) {
    if (!window._learnq) {
      console.warn('Klaviyo tracking not loaded');
      return;
    }

    window._learnq.push(['track', eventName, properties]);
  }

  identifyKlaviyoUser(email, properties = {}) {
    if (!window._learnq) return;

    window._learnq.push(['identify', {
      '$email': email,
      ...properties
    }]);
  }

  storeSubscriptionLocally(data) {
    const subscriptions = JSON.parse(localStorage.getItem('newsletter_subscriptions') || '[]');
    subscriptions.push({
      ...data,
      subscribedAt: new Date().toISOString()
    });
    localStorage.setItem('newsletter_subscriptions', JSON.stringify(subscriptions));
  }

  // Utility methods
  getCookie(name) {
    const value = '; ' + document.cookie;
    const parts = value.split('; ' + name + '=');
    if (parts.length === 2) return parts.pop().split(';').shift();
  }

  setCookie(name, value, days) {
    const expires = new Date(Date.now() + days * 864e5).toUTCString();
    document.cookie = name + '=' + encodeURIComponent(value) + '; expires=' + expires + '; path=/';
  }

  showSuccess(message) {
    this.showToast(message, 'success');
  }

  showError(message) {
    this.showToast(message, 'error');
  }

  showToast(message, type = 'info') {
    const toast = document.createElement('div');
    toast.className = 'klaviyo-toast klaviyo-toast--' + type;
    toast.textContent = message;
    document.body.appendChild(toast);

    setTimeout(() => toast.classList.add('klaviyo-toast--visible'), 10);
    setTimeout(() => {
      toast.classList.remove('klaviyo-toast--visible');
      setTimeout(() => toast.remove(), 300);
    }, 3000);
  }
}

// Initialize
new KlaviyoIntegration();
